import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText } from "lucide-react";
import { StatusPill, PrimaryButton } from "@/components/brand";
import api from "@/lib/api";

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">{k}</span>
      <span className="text-sm text-[#0a0a0a] text-right">{v}</span>
    </div>
  );
}

export default function ApplicantDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/applications/${id}`);
    setA(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    if (status === "rejected" && !reason.trim()) return toast.error("Add a reason before rejecting.");
    setBusy(true);
    try { await api.patch(`/applications/${id}/status`, { status, reject_reason: reason }); toast.success(`Marked ${status.replace("_", " ")}`); await load(); }
    catch { toast.error("Action failed"); }
    setBusy(false);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/applications/${id}/notes`, { note }); setNote(""); await load(); toast.success("Note added"); }
    catch { toast.error("Failed"); }
  };

  const viewDoc = async (fid) => {
    setViewing(fid);
    try { const res = await api.get(`/files/${fid}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); } catch {}
    setViewing("");
  };

  if (!a) return <div className="text-sm text-gray-400 font-mono uppercase tracking-wide">Loading…</div>;

  return (
    <div data-testid="applicant-detail">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0a0a0a] mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to queue
      </Link>

      <div className="flex items-center gap-4 mb-2">
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Applicant Review</span>
        <StatusPill status={a.status} />
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-[#0a0a0a] mb-8">{a.first_name} {a.last_name}</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-3">01 · Operator identity</div>
            <div className="border border-gray-200 divide-y divide-gray-200">
              <Row k="Email" v={a.email} /><Row k="Phone" v={a.phone} />
              <Row k="City" v={`${a.city}, ${a.province}`} /><Row k="Region" v={a.region} />
            </div>
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-3">02 · Licence & vehicle</div>
            <div className="border border-gray-200 divide-y divide-gray-200">
              <Row k="Licence" v={`${a.licence_class} · ${a.licence_number}`} />
              <Row k="Licence expiry" v={new Date(a.licence_expiry).toLocaleDateString()} />
              <Row k="Vehicle" v={`${a.vehicle?.year} ${a.vehicle?.make} ${a.vehicle?.model}`} />
              <Row k="Plate / type" v={`${a.vehicle?.plate} · ${a.vehicle?.type}`} />
              <Row k="Insurance" v={`${a.insurance?.provider} · ${a.insurance?.policy}`} />
            </div>
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-3">03 · Documents</div>
            <div className="border border-gray-200 divide-y divide-gray-200">
              {(!a.documents || a.documents.length === 0) && <div className="px-5 py-6 text-center text-sm text-gray-400">No documents uploaded.</div>}
              {(a.documents || []).map((doc) => (
                <div key={doc.file_id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-gray-400" /><div><div className="text-sm text-[#0a0a0a] capitalize">{doc.type}</div><div className="text-xs text-gray-500">{doc.filename}</div></div></div>
                  <button onClick={() => viewDoc(doc.file_id)} data-testid={`view-doc-${doc.file_id}`} className="font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 hover:underline">{viewing === doc.file_id ? "Opening…" : "View"}</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Decision + audit notes */}
        <div className="space-y-6">
          <div className="border border-gray-200 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-4">Decision</div>
            <div className="space-y-2">
              {a.status === "pending" && (
                <button onClick={() => setStatus("under_review")} data-testid="detail-review" disabled={busy} className="w-full px-6 py-3 text-sm font-semibold border border-gray-300 hover:border-[#0a0a0a] transition-colors">Start review</button>
              )}
              <PrimaryButton onClick={() => setStatus("approved")} data-testid="detail-approve" disabled={busy} className="w-full">Approve applicant</PrimaryButton>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" rows={2} placeholder="Rejection reason…" className="routa-input" />
              <button onClick={() => setStatus("rejected")} data-testid="detail-reject" disabled={busy} className="w-full px-6 py-3 text-sm font-semibold border border-orange-500 text-orange-600 hover:bg-orange-50 transition-colors">Reject applicant</button>
            </div>
          </div>

          <div className="border border-gray-200 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-4">Audit notes</div>
            <div className="space-y-3 mb-4" data-testid="audit-notes">
              {(a.audit_notes || []).length === 0 && <div className="text-sm text-gray-400">No notes yet.</div>}
              {(a.audit_notes || []).map((n, i) => (
                <div key={i} className="border-l-2 border-orange-500 pl-3">
                  <div className="text-sm text-[#0a0a0a]">{n.note}</div>
                  <div className="font-mono text-[10px] text-gray-400 mt-0.5">{n.by} · {new Date(n.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" rows={2} placeholder="Add an audit note…" className="routa-input mb-2" />
            <button onClick={addNote} data-testid="note-submit" className="w-full px-4 py-2 text-sm font-semibold bg-[#0a0a0a] text-white hover:bg-[#262626] transition-colors">Add note</button>
          </div>
        </div>
      </div>
    </div>
  );
}
